<template>
    <div id="dashboard-projects-sidebar-view">
        <template v-if="selectedProject">
            <div class="mb-10 relative d-flex justify-center">
                <div v-if="!editingName" class="dashboard-subtitle inline-edit-label-container position-relative">
                    <span class="inline-edit-label">
                        {{ selectedProject.name }}
                    </span>
                    <v-icon
                        @click="startEditing('name')"
                        class="color-gray ml-2 inline-edit-icon"
                    >mdi mdi-pencil
                    </v-icon>
                </div>

                <v-text-field
                    v-else
                    v-model="form.name"
                    ref="name"
                    class="dashboard-subtitle color-gray inline-edit-input text-center"
                    dark
                    hide-details
                    :disabled="!editingName"
                    @blur="endEditing('name')"
                    @keydown="updateName($event, 'name')"
                />
            </div>

            <span class="dashboard-text d-block mb-10">You can assign a user or a guest to this project using the toggle on the right.</span>
            <div class="d-flex justify-space-between mb-2">
                <span class="dashboard-list-title">PROJECT MEMBERS</span>
                <span class="dashboard-list-title">ASSIGNED</span>
            </div>

            <v-list class="mb-10">
                <v-list-item v-for="user in activeUsers" :key="user.id" class="pa-0">
                    <v-list-item-content>
                        <v-list-item-title class="color-gray">
                            {{ user.user.name }} ({{ user.role_name }})
                            <span class="color-warning" v-if="user.suspended_at"> (suspended)</span>
                        </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <v-switch
                            v-if="showSwitch(user)"
                            :disabled="!user.accepted || !canAssignUsersToProject(selectedProject)"
                            :input-value="isUserAssigned(user)"
                            class="ml-1 text-center"
                            color="var(--primary)"
                            inset
                            dark
                            @click="toggleUser(user)"
                        />
                    </v-list-item-action>
                </v-list-item>
            </v-list>

            <template v-if="activeGuests && activeGuests.length > 0">
                <div class="d-flex justify-space-between mb-2">
                    <span class="dashboard-list-title">GUESTS</span>
                    <span class="dashboard-list-title">ASSIGNED</span>
                </div>

                <v-list class="mb-5">
                    <v-list-item v-for="user in activeGuests" :key="user.id" class="pa-0">
                        <v-list-item-content>
                            <v-list-item-title class="color-gray">{{ user.user.email }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-switch
                                :disabled="!user.accepted || !canAssignUsersToProject(selectedProject)"
                                :input-value="isUserAssigned(user)"
                                class="ml-1 text-center"
                                color="var(--primary)"
                                inset
                                dark
                                @click="toggleUser(user)"
                            />
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </template>


            <template v-if="canUseGroups">
                <span class="dashboard-list-title mb-3 align-self-start">GROUPS</span>

                <v-list class="mb-5">
                    <v-list-item v-for="group in groups" :key="group.id">
                        <v-list-item-content>
                            <v-list-item-title style="font-size: 13px; color: #989898">{{
                                    group.name
                                }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-switch
                                :disabled="!canAssignUsersToProject(selectedProject)"
                                :input-value="isGroupAssigned(group)"
                                class="ml-1 text-center"
                                color="var(--primary)"
                                inset
                                dark
                                @click="toggleGroup(group)"
                            />
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </template>

            <v-dialog v-if="canDeleteProject" v-model="dialog"
                      content-class="dashboard-dialog"
                      max-width="450px"
                      transition="fade-transition"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="btn-danger rounded-xl mt-10 d-block mx-auto" width="200px" outlined large>
                        DELETE PROJECT
                    </v-btn>
                </template>

                <v-card>
                    <v-card-title>
                        <span>Delete Project "{{ selectedProject.name }}"?</span>
                    </v-card-title>
                    <v-card-text class="mb-5">
                        <p class="text-center mb-4">Deleting a project is irreversible. If you are sure, type DELETE in the following box and confirm.</p>
                        <input type="text" v-model="confirmDelete" required />
                    </v-card-text>
                    <v-card-actions>
                        <v-btn :disabled="confirmDelete != 'DELETE'" class="btn-danger mr-5" text
                               @click="deleteProject"
                        >
                            Confirm
                        </v-btn>

                        <v-btn class="--cancel" text @click="dialog = false">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import * as rules from "vuelidate/lib/validators";
import CanMixin from "@/mixins/CanMixin";

export default {
    mixins: [CanMixin],
    name: "ProjectsSidebar",

    computed: {
        ...mapState("Projects", ["selectedProject"]),
        ...mapGetters("Organizations", ["activeUsers", "activeGuests"]),
        ...mapState("User", ["account"]),
        ...mapState("Groups", ["groups"]),
    },
    data() {
        return {
            editingName: false,
            confirmDelete: null,
            dialog: false,
            form: new Form(this, "form", {
                name: null,
            }),
        };
    },

    validations: {
        form: {
            name: { required: rules.required },
        },
    },

    watch: {
        '$store.state.Projects.selectedProject': {
            immediate: true,
            handler() {
                if (this.selectedProject) {
                    this.form.name = this.selectedProject.name;

                }
            }
        },
    },

    methods: {
        startEditing(data) {
            if (data == 'name') this.editingName = true;

            this.$nextTick(() => {
                this.$refs[data].$el.querySelector('input').focus();
            })
        },

        endEditing(data) {
            if (data == 'name') this.editingName = false;
        },

        updateName(e, data) {
            if (e.key == "Enter" || e.keyCode == 13) {
                this.endEditing(data);
                if (!this.form.validate()) return;

                this.$apiService.patch(`accounts/${this.account.id}/projects/${this.selectedProject.id}`, this.form);
                this.$store.dispatch("Projects/fetchProjects");
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endEditing(data);
            }
        },

        isUserAssigned(user) {
            if (this.selectedProject.users.find(({ id }) => id === user.user.id)) return true;
            else return false;
        },

        toggleUser(user) {
            if (this.isUserAssigned(user)) this.removeUserFromProject(user);
            else this.addUserToProject(user);
        },

        async addUserToProject(user) {
            await this.$store.dispatch("Projects/assignUsers", {
                project_id: this.selectedProject.id,
                account_ids: [user.id],
            });

            this.$store.dispatch("Projects/fetchProjects");
        },

        async removeUserFromProject(user) {
            await this.$store.dispatch("Projects/removeUsers", {
                project_id: this.selectedProject.id,
                account_ids: [user.id],
            });

            this.$store.dispatch("Projects/fetchProjects");
        },

        isGroupAssigned(group) {
            if (this.selectedProject.groups.find(({ id }) => id === group.id)) return true;
            else return false;
        },

        toggleGroup(group) {
            if (this.isGroupAssigned(group)) this.removeGroupFromProject(group);
            else this.addGroupToProject(group);
        },

        async addGroupToProject(group) {
            await this.$store.dispatch("Projects/assignGroups", {
                project_id: this.selectedProject.id,
                group_name: [group.name],
            });

            this.$store.dispatch("Projects/fetchProjects");
        },

        async removeGroupFromProject(group) {
            await this.$store.dispatch("Projects/removeGroups", {
                project_id: this.selectedProject.id,
                group_name: [group.name],
            });

            this.$store.dispatch("Projects/fetchProjects");
        },

        async deleteProject() {
            this.dialog = false;
            this.confirmDelete = null;

            await this.$store.dispatch("Projects/deleteProject", {
                project_id: this.selectedProject.id,
            });

            this.$store.dispatch("Projects/fetchProjects");
            // this.$store.dispatch("Dashboard/resetState");
        },

        showSwitch(user) {
            if (user.role_id == 2) return false;
            if (user.role_id == 3 && this.account.role_id == 4) return false;

            return true;
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep .v-list {
    background: transparent;
    color: gray;
}

.inline-edit-label-container {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    .inline-edit-label {
        max-width: 60%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.25;
    }

    .inline-edit-icon {
        //position: absolute;
        //top: 50%;
        ////right: -20px;
        //right: -20px;
        //transform: translateY(-50%);
    }
}

::v-deep .inline-edit-input {
    position: relative;

    input {
        font-size: 16px;
        text-align: center;
    }

    .v-input__append-outer {
        position: absolute;
        right: 0;
        margin: 0;
    }
}
</style>